import { useEffect, useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  adminLogin,
  adminMe,
  getToken,
  setToken,
  clearToken,
} from "@/lib/api";

/**
 * Gates children behind a passcode. Stores a long-lived token in localStorage.
 * On 401 from any API call, the axios interceptor will clear the token and
 * dispatch "aql:auth-changed", which we listen for here to re-prompt.
 */
export default function AdminGate({ children }) {
  const [state, setState] = useState("checking"); // 'checking' | 'locked' | 'unlocked'
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const verify = async () => {
    if (!getToken()) {
      setState("locked");
      return;
    }
    try {
      await adminMe();
      setState("unlocked");
    } catch {
      setState("locked");
    }
  };

  useEffect(() => {
    verify();
    const onAuthChanged = () => {
      if (!getToken()) setState("locked");
    };
    window.addEventListener("aql:auth-changed", onAuthChanged);
    return () => window.removeEventListener("aql:auth-changed", onAuthChanged);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      const { token } = await adminLogin(code.trim());
      setToken(token);
      setCode("");
      setState("unlocked");
      toast.success("Welcome back");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many attempts. Try again in a few minutes.");
      } else {
        toast.error("Incorrect passcode");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "checking") {
    return (
      <main
        data-testid="admin-checking"
        className="min-h-screen bg-[#05080F] text-white flex items-center justify-center"
      >
        <Loader2 className="animate-spin text-[#E5A93B]" size={22} />
      </main>
    );
  }

  if (state === "locked") {
    return (
      <main
        data-testid="admin-locked"
        className="min-h-screen bg-[#05080F] text-white flex items-center justify-center px-6"
      >
        <div className="relative w-full max-w-md">
          <div className="absolute -top-12 left-0 flex items-baseline gap-3">
            <span className="font-mono text-xs tracking-[0.3em] text-[#E5A93B]">
              AQUA / AERIALS
            </span>
            <span className="font-mono text-xs tracking-[0.3em] text-white/40">
              / ADMIN
            </span>
          </div>
          <form
            onSubmit={onSubmit}
            data-testid="admin-login-form"
            className="bg-[#0D1322] border border-white/10 p-8 md:p-10"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 bg-[#E5A93B]/10 border border-[#E5A93B]/40 flex items-center justify-center">
                <Lock size={16} className="text-[#E5A93B]" />
              </div>
              <div>
                <span className="overline text-white/60">Private</span>
                <h1 className="text-2xl font-bold tracking-tight">
                  Enter admin passcode
                </h1>
              </div>
            </div>
            <Input
              data-testid="admin-passcode-input"
              type="password"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="••••"
              className="bg-[#05080F] border-white/10 text-white rounded-none h-12 text-center font-mono tracking-[0.4em] text-lg focus-visible:ring-[#E5A93B] focus-visible:ring-offset-0"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-passcode-submit"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#E5A93B] hover:bg-[#FACC15] disabled:opacity-50 text-black px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              Unlock Admin
            </button>
            <p className="mt-6 font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 text-center">
              Only the owner has this code.
            </p>
          </form>
        </div>
      </main>
    );
  }

  return (
    <>
      {children}
      <button
        data-testid="admin-logout"
        onClick={() => {
          clearToken();
          setState("locked");
          toast.success("Logged out");
        }}
        className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 bg-[#0D1322] border border-white/10 text-white/70 hover:text-[#E5A93B] hover:border-[#E5A93B]/40 px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors"
      >
        <Lock size={12} />
        Logout
      </button>
    </>
  );
}
